import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Visit, VisitFilters, VisitStatus, VisitStats } from '@/types/visit.types';
import { PaginatedResponse } from '@/types';
import { visitsService } from '@/services/visits.service';
import { Button } from '@/components/ui/Button';
import {
  VisitStatusBadge,
  getAllVisitStatuses,
  VisitCard,
  TokenEntryModal,
} from '@/components/visits';

export const VisitList: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [visits, setVisits] = useState<Visit[]>([]);
  const [stats, setStats] = useState<VisitStats | null>(null);
  const [meta, setMeta] = useState<PaginatedResponse<Visit>['meta'] | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Token modal state
  const [selectedVisit, setSelectedVisit] = useState<Visit | null>(null);
  const [showTokenModal, setShowTokenModal] = useState(false);
  const [tokenError, setTokenError] = useState<string | null>(null);
  const [isCompletingVisit, setIsCompletingVisit] = useState(false);

  // Filters from URL params
  const [filters, setFilters] = useState<VisitFilters>({
    status: (searchParams.get('status') as VisitStatus) || undefined,
    date_from: searchParams.get('date_from') || undefined,
    date_to: searchParams.get('date_to') || undefined,
    sales_kit_id: searchParams.get('sales_kit_id') || undefined,
  });

  const [page, setPage] = useState(Number(searchParams.get('page')) || 1);

  const fetchVisits = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const cleanFilters: VisitFilters = {};
      if (filters.status) cleanFilters.status = filters.status;
      if (filters.date_from) cleanFilters.date_from = filters.date_from;
      if (filters.date_to) cleanFilters.date_to = filters.date_to;
      if (filters.sales_kit_id) cleanFilters.sales_kit_id = filters.sales_kit_id;

      const [visitsResponse, statsResponse] = await Promise.all([
        visitsService.getAll(cleanFilters, page, 20),
        visitsService.getStats(),
      ]);

      setVisits(visitsResponse.data?.data ?? []);
      setMeta(visitsResponse.data?.meta ?? null);
      setStats(statsResponse.data ?? null);
    } catch (err) {
      setError('Failed to load visits');
      console.error('Error fetching visits:', err);
    } finally {
      setIsLoading(false);
    }
  }, [filters, page]);

  useEffect(() => {
    fetchVisits();
  }, [fetchVisits]);

  // Update URL params when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    if (filters.status) params.set('status', filters.status);
    if (filters.date_from) params.set('date_from', filters.date_from);
    if (filters.date_to) params.set('date_to', filters.date_to);
    if (filters.sales_kit_id) params.set('sales_kit_id', filters.sales_kit_id);
    if (page > 1) params.set('page', String(page));
    setSearchParams(params, { replace: true });
  }, [filters, page, setSearchParams]);

  const handleFilterChange = (key: keyof VisitFilters, value: string) => {
    setFilters({
      ...filters,
      [key]: value === '' ? undefined : value,
    });
    setPage(1);
  };

  const clearFilters = () => {
    setFilters({});
    setPage(1);
  };

  const hasActiveFilters = filters.status || filters.date_from || filters.date_to || filters.sales_kit_id;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const formatTime = (timeString?: string) => {
    if (!timeString) return '-';
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  // Visit actions
  const handleCompleteVisit = (visit: Visit) => {
    setSelectedVisit(visit);
    setTokenError(null);
    setShowTokenModal(true);
  };

  const handleTokenSubmit = async (token: string) => {
    if (!selectedVisit) return;

    try {
      setIsCompletingVisit(true);
      setTokenError(null);
      await visitsService.complete(selectedVisit.id, token);
      await fetchVisits();
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } } };
      const errorMessage = error.response?.data?.message || 'Invalid or expired token';
      setTokenError(errorMessage);
      throw err;
    } finally {
      setIsCompletingVisit(false);
    }
  };

  const handleReschedule = (visit: Visit) => {
    navigate(`/visits/${visit.id}?action=reschedule`);
  };

  const handleCancel = async (visit: Visit) => {
    if (!confirm('Are you sure you want to cancel this visit?')) return;

    try {
      await visitsService.cancel(visit.id);
      await fetchVisits();
    } catch (err) {
      console.error('Error cancelling visit:', err);
      toast.error('Failed to cancel visit');
    }
  };

  const handleMarkNoShow = async (visit: Visit) => {
    if (!confirm('Mark this visit as No Show?')) return;

    try {
      await visitsService.markNoShow(visit.id);
      await fetchVisits();
    } catch (err) {
      console.error('Error marking no show:', err);
      toast.error('Failed to mark as no show');
    }
  };

  const statusOptions = [
    { value: '', label: 'All Statuses' },
    ...getAllVisitStatuses(),
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900">Visits</h1>
          <p className="text-secondary-600 mt-1">
            Manage and track property visits
          </p>
        </div>
        <Button
          onClick={() => navigate('/visits/schedule')}
          leftIcon={
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
          }
        >
          Schedule Visit
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          <div className="bg-white rounded-xl border border-secondary-200 shadow-sm p-4">
            <p className="text-sm text-secondary-500">Total Visits</p>
            <p className="text-2xl font-bold text-secondary-900 mt-1">{stats.total}</p>
          </div>
          <div className="bg-white rounded-xl border border-secondary-200 shadow-sm p-4">
            <p className="text-sm text-secondary-500">Scheduled</p>
            <p className="text-2xl font-bold text-blue-600 mt-1">{stats.scheduled}</p>
          </div>
          <div className="bg-white rounded-xl border border-secondary-200 shadow-sm p-4">
            <p className="text-sm text-secondary-500">Completed</p>
            <p className="text-2xl font-bold text-green-600 mt-1">{stats.completed}</p>
          </div>
          <div className="bg-white rounded-xl border border-secondary-200 shadow-sm p-4">
            <p className="text-sm text-secondary-500">No Shows</p>
            <p className="text-2xl font-bold text-orange-600 mt-1">{stats.no_shows}</p>
          </div>
          <div className="bg-white rounded-xl border border-secondary-200 shadow-sm p-4 col-span-2 lg:col-span-1">
            <p className="text-sm text-secondary-500">Conversion Rate</p>
            <p className="text-2xl font-bold text-primary-600 mt-1">{stats.conversion_rate}%</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl border border-secondary-200 shadow-sm p-4">
        <div className="flex flex-wrap gap-3">
          <select
            value={filters.status || ''}
            onChange={(e) => handleFilterChange('status', e.target.value)}
            className="px-4 py-2 border border-secondary-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            {statusOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          <input
            type="date"
            value={filters.date_from || ''}
            onChange={(e) => handleFilterChange('date_from', e.target.value)}
            placeholder="From Date"
            className="px-4 py-2 border border-secondary-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />

          <input
            type="date"
            value={filters.date_to || ''}
            onChange={(e) => handleFilterChange('date_to', e.target.value)}
            placeholder="To Date"
            className="px-4 py-2 border border-secondary-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />

          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="px-4 py-2 text-sm text-secondary-600 hover:text-secondary-900 hover:bg-secondary-100 rounded-lg transition-colors"
            >
              Clear Filters
            </button>
          )}
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="flex flex-col items-center space-y-4">
            <div className="w-12 h-12 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin"></div>
            <p className="text-secondary-600">Loading visits...</p>
          </div>
        </div>
      )}

      {/* Error State */}
      {error && !isLoading && (
        <div className="text-center py-12">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-100 mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-secondary-900 mb-2">Failed to load visits</h3>
          <p className="text-secondary-600 mb-4">{error}</p>
          <Button onClick={fetchVisits}>Try Again</Button>
        </div>
      )}

      {/* Empty State */}
      {!isLoading && !error && visits.length === 0 && (
        <div className="text-center py-12">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-secondary-100 mb-4">
            <svg className="w-8 h-8 text-secondary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-secondary-900 mb-2">
            {hasActiveFilters ? 'No visits found' : 'No visits yet'}
          </h3>
          <p className="text-secondary-600 mb-4">
            {hasActiveFilters
              ? 'Try adjusting your filters'
              : 'Schedule your first visit to get started'}
          </p>
          {!hasActiveFilters && (
            <Button onClick={() => navigate('/visits/schedule')}>Schedule Visit</Button>
          )}
        </div>
      )}

      {/* Visits List */}
      {!isLoading && !error && visits.length > 0 && (
        <>
          {/* Desktop Table View */}
          <div className="hidden lg:block bg-white rounded-xl border border-secondary-200 shadow-sm overflow-hidden">
            <table className="min-w-full divide-y divide-secondary-200">
              <thead className="bg-secondary-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                    Visit #
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                    Lead
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                    Project
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                    Date / Time
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-secondary-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-secondary-200">
                {visits.map((visit) => (
                  <tr
                    key={visit.id}
                    onClick={() => navigate(`/visits/${visit.id}`)}
                    className="hover:bg-secondary-50 cursor-pointer transition-colors"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <p className="text-sm font-medium text-secondary-900">
                        #{visit.visit_number}
                      </p>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <p className="text-sm font-medium text-secondary-900">{visit.lead.name}</p>
                      <p className="text-sm text-secondary-500">{visit.lead.phone}</p>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <p className="text-sm text-secondary-600">
                        {visit.sales_kit?.name || '-'}
                      </p>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <p className="text-sm text-secondary-900">{formatDate(visit.scheduled_date)}</p>
                      <p className="text-sm text-secondary-500">{formatTime(visit.scheduled_time)}</p>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <VisitStatusBadge status={visit.status} size="sm" />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      {visit.status === 'SCHEDULED' && (
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCompleteVisit(visit);
                            }}
                            className="px-3 py-1.5 text-xs font-medium text-green-700 bg-green-50 rounded-md hover:bg-green-100 transition-colors"
                          >
                            Complete
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleReschedule(visit);
                            }}
                            className="px-3 py-1.5 text-xs font-medium text-purple-700 bg-purple-50 rounded-md hover:bg-purple-100 transition-colors"
                          >
                            Reschedule
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleMarkNoShow(visit);
                            }}
                            className="px-3 py-1.5 text-xs font-medium text-orange-700 bg-orange-50 rounded-md hover:bg-orange-100 transition-colors"
                          >
                            No Show
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCancel(visit);
                            }}
                            className="px-3 py-1.5 text-xs font-medium text-red-700 bg-red-50 rounded-md hover:bg-red-100 transition-colors"
                          >
                            Cancel
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View */}
          <div className="lg:hidden grid gap-4 sm:grid-cols-2">
            {visits.map((visit) => (
              <VisitCard
                key={visit.id}
                visit={visit}
                onComplete={handleCompleteVisit}
                onReschedule={handleReschedule}
                onCancel={handleCancel}
                onMarkNoShow={handleMarkNoShow}
              />
            ))}
          </div>

          {/* Pagination */}
          {meta && meta.totalPages > 1 && (
            <div className="flex items-center justify-between bg-white rounded-xl border border-secondary-200 shadow-sm px-4 py-3">
              <div className="flex items-center text-sm text-secondary-600">
                Showing {(meta.page - 1) * meta.limit + 1} to{' '}
                {Math.min(meta.page * meta.limit, meta.total)} of {meta.total} visits
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                >
                  Previous
                </Button>
                <div className="flex items-center gap-1">
                  {Array.from({ length: meta.totalPages }, (_, i) => i + 1)
                    .filter((p) => p === 1 || p === meta.totalPages || Math.abs(p - page) <= 1)
                    .map((p, idx, arr) => (
                      <React.Fragment key={p}>
                        {idx > 0 && arr[idx - 1] !== p - 1 && (
                          <span className="px-2 text-secondary-400">...</span>
                        )}
                        <button
                          onClick={() => setPage(p)}
                          className={`w-8 h-8 text-sm font-medium rounded-lg transition-colors ${
                            p === page
                              ? 'bg-primary-600 text-white'
                              : 'text-secondary-600 hover:bg-secondary-100'
                          }`}
                        >
                          {p}
                        </button>
                      </React.Fragment>
                    ))}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(page + 1)}
                  disabled={page === meta.totalPages}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Token Entry Modal */}
      <TokenEntryModal
        isOpen={showTokenModal}
        onClose={() => {
          setShowTokenModal(false);
          setSelectedVisit(null);
          setTokenError(null);
        }}
        onSubmit={handleTokenSubmit}
        visitNumber={selectedVisit?.visit_number || ''}
        isLoading={isCompletingVisit}
        error={tokenError}
      />
    </div>
  );
};

export default VisitList;
